<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useScheduledActionsStore } from '~/stores/scheduled-actions';
import { useToast } from 'primevue/usetoast';
import { cleanText } from '~/Common/StringOperations';
import { useI18n } from 'vue-i18n';
import type { PeriodicActionSet, ClientGroup, Client } from '~/types/graphql';
import ActionScheduler from '~/components/base/forms/ActionScheduler.vue';

const {t} = useI18n();
const scheduledActionsStore = useScheduledActionsStore();
const toast = useToast();
const loading = computed(() => scheduledActionsStore.loading);
const { actions } = storeToRefs(scheduledActionsStore);
const selectedAction = ref<PeriodicActionSet | null>(null);
const editDialog = ref(false);
const viewDialog = ref(false);

onMounted(async () => {
  await scheduledActionsStore.ensureLoaded();
});

async function loadActions() {
  try {
    await scheduledActionsStore.getAllActions();
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load scheduled actions',
      life: 3000
    });
  }
}


function viewAction(action: PeriodicActionSet) {
  selectedAction.value = { ...action };
  viewDialog.value = true;
}

function editAction(action: PeriodicActionSet) {
  selectedAction.value = { ...action };
  editDialog.value = true;
}

async function onActionSaved() {
  editDialog.value = false;
  selectedAction.value = null;
  await loadActions();
}

async function deleteAction(action: PeriodicActionSet) {
  try {
    const result = await scheduledActionsStore.deleteAction(action.id);
    if (result === null || !result.deleteScheduledAction) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete action', life: 3000 });
    } else {
      toast.add({ severity: 'success', summary: 'Success', detail: 'Action deleted successfully', life: 3000 });
    }
  } catch (error) {
    console.error('Delete error:', error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete action', life: 3000 });
  }
}

function formatDate(date: string | Date): string {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
}

</script>

<template>
  <div class="scheduled-actions-container">
    <DataTable
      :value="actions"
      :loading="loading"
      paginator
      :rows="10"
      :rowsPerPageOptions="[10, 20, 50]"
      responsiveLayout="scroll"
      stripedRows
      class="p-datatable-sm"
    >
      <Column field="type" :header="t('scheduler.actionType')" sortable>
        <template #body="slotProps">
          {{ cleanText(slotProps.data.type) }}
        </template>
      </Column>
      <Column field="description" :header="t('scheduler.description')"></Column>
      <Column field="targetName" header="Target" sortable>
        <template #body="slotProps">
        <span v-if="slotProps.data.clients?.length">
          {{ slotProps.data.clients.map((c: Client) => c.hostname || c.hostname).join(', ') }}
        </span>
          <span v-else-if="slotProps.data.clientGroups?.length">
          {{ slotProps.data.clientGroups.map((g: ClientGroup) => g.name).join(', ') }}
        </span>
          <span v-else>N/A</span>
        </template>
      </Column>
      <Column field="delegateClient" header="Delegate Client">
        <template #body="slotProps">
          <span v-if="slotProps.data.delegateClient">
            <Tag severity="info" :value="slotProps.data.delegateClient.hostname" />
          </span>
          <span v-else class="text-gray-500">—</span>
        </template>
      </Column>
      <Column field="createdAt" :header="t('scheduler.createdAt')" sortable>
        <template #body="slotProps">
          {{ formatDate(slotProps.data.createdAt) }}
        </template>
      </Column>
      <Column header="Actions">
        <template #body="slotProps">
          <div class="flex gap-2">
            <Button icon="pi pi-eye" text rounded severity="info" @click="viewAction(slotProps.data)" />
            <Button icon="pi pi-pencil" text rounded severity="warning" @click="editAction(slotProps.data)" />
            <Button icon="pi pi-trash" text rounded severity="danger" @click="deleteAction(slotProps.data)" />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- View Dialog -->
    <Dialog v-model:visible="viewDialog" header="View Scheduled Action" :style="{width: '450px'}" :modal="true">
      <div v-if="selectedAction" class="p-fluid">
        <div class="field mb-3">
          <label>{{ t('scheduler.actionType') }}</label>
          <div>{{ selectedAction.type }}</div>
        </div>
        <div class="field mb-3">
          <label>Target</label>
          <div>
          <span v-if="selectedAction.clients?.length">
            {{ selectedAction.clients.map((c: Client) => c.hostname).join(', ') }}
          </span>
            <span v-else-if="selectedAction.clientGroups?.length">
            {{ selectedAction.clientGroups.map((g: ClientGroup) => g.name).join(', ') }}
          </span>
            <span v-else>N/A</span>
          </div>
        </div>
        <div v-if="selectedAction.delegateClient" class="field mb-3">
          <label>{{ t('scheduler.delegateClient') }}</label>
          <div>{{ selectedAction.delegateClient.hostname }}</div>
        </div>
        <div class="field mb-3">
          <label>Description</label>
          <div>{{ selectedAction.description }}</div>
        </div>
        <div class="field mb-3">
          <label>Created</label>
          <div>{{ formatDate(selectedAction.createdAt) }}</div>
        </div>
      </div>
    </Dialog>

    <!-- Edit Dialog - Using ActionScheduler Component -->
    <ActionScheduler
      v-if="selectedAction"
      :is-open="editDialog"
      @update:is-open="editDialog = $event"
      @saved="onActionSaved"
      :action-id="selectedAction.id"
      :actionType="selectedAction.type"
      :description="selectedAction.description"
      :extra-data="selectedAction.extraData"
      :start-at="selectedAction.startAt"
      :end-at="selectedAction.endAt"
      :cron-expression="selectedAction.cronExpression"
      :timezone="selectedAction.timezone"
    />
  </div>
</template>

<style scoped>
.scheduled-actions-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.p-datatable thead th) {
  border-bottom: 1px solid hsl(var(--border)) !important;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  border-bottom: 1px solid hsl(var(--border)) !important;
}

</style>
